/**
 * Testnet Pool Configuration
 *
 * Deployed TestnetSwapPool.sol addresses, testnet token addresses,
 * ABI encoding helpers, and Canton bridge party management.
 *
 * All EVM and Canton operations go through the Stratos Wallet SDK
 * (postMessage bridge to the parent portal). No direct API calls.
 */

// ---------------------------------------------------------------------------
// SDK interface (subset of StratosSDK used here)
// ---------------------------------------------------------------------------
export interface SDKLike {
  evmCall(params: { method: string; params: unknown[]; chainId: number }): Promise<{ result: string }>;
  cantonQuery<T = Record<string, unknown>>(params: {
    templateId: string;
    filter?: Record<string, unknown>;
    readAs?: string[];
  }): Promise<Array<{ contractId: string; templateId: string; payload: T }>>;
  cantonExercise(params: {
    contractId: string;
    templateId: string;
    choice: string;
    argument: Record<string, unknown>;
    actAs?: string[];
  }): Promise<{ exerciseResult: unknown }>;
  acceptBridgeTransferOffers(): Promise<{ accepted: number; errors: string[]; ccBalance?: string }>;
  transfer(params: { to: string; amount: string; symbol: string; chain: string }): Promise<{ txId: string; status: string }>;
  callAPI<T = unknown>(path: string, body?: Record<string, unknown>): Promise<T>;
  getTransferOffers(): Promise<Array<{ contractId: string; sender: string; receiver: string; amount: string; symbol: string }>>;
  acceptTransferOffer(contractId: string): Promise<{ txId: string; status: string }>;
}

// ---------------------------------------------------------------------------
// Canton Daml package & template IDs
// ---------------------------------------------------------------------------
export const CANTON_PACKAGE_ID =
  '667c8afcfe4f0084bc63de4e46de0d0f2c7c3a6a911fa392cafcb0b842097a6a';

export const CANTON_TEMPLATES = {
  BridgeEscrow: `${CANTON_PACKAGE_ID}:CantonCoin.Bridge:BridgeEscrow`,
  BridgeRequest: `${CANTON_PACKAGE_ID}:CantonCoin.Bridge:BridgeRequest`,
  BridgeConfig: `${CANTON_PACKAGE_ID}:CantonCoin.Bridge:BridgeConfig`,
  BridgeEscrowReceipt: `${CANTON_PACKAGE_ID}:CantonCoin.Bridge:BridgeEscrowReceipt`,
  BridgeRequestReceipt: `${CANTON_PACKAGE_ID}:CantonCoin.Bridge:BridgeRequestReceipt`,
} as const;

// Canton USDC (USDCHolding)
// Format: "<package_id>:CantonUSDC.Token:USDCHolding"
export let USDC_HOLDING_TEMPLATE_ID = '3b6cd0197bc2cd409db5f689f0ee07638cb0a73307196f729280c6924a431c9e:CantonUSDC.Token:USDCHolding';

// Splice Amulet (CC) template — different package from TransferOffer
export const AMULET_TEMPLATE_ID = '3ca1343ab26b453d38c8adb70dca5f1ead8440c42b59b68f070786955cbf9ec1:Splice.Amulet:Amulet';

export function setUsdcHoldingTemplateId(templateId: string): void {
  USDC_HOLDING_TEMPLATE_ID = templateId;
}

// ---------------------------------------------------------------------------
// Bridge operator party (hardcoded — created via shell, not from mini-app)
// ---------------------------------------------------------------------------
const BRIDGE_OPERATOR_PARTY = 'BridgeOperator::12201bcc9104def63800ea971e66d1a2d8565f4cf35d334bd5093882c31197d5988d';

/**
 * Get the bridge operator party ID.
 * Returns the dedicated bridge party if set, otherwise falls back to
 * the provided user party (user-as-operator model for testnet).
 */
export function getBridgeOperatorParty(userPartyFallback: string): string {
  return BRIDGE_OPERATOR_PARTY || userPartyFallback;
}

// ---------------------------------------------------------------------------
// Bridge config contract creation helper
// ---------------------------------------------------------------------------

export interface BridgeConfigPayload {
  [key: string]: unknown;
  admin: string;
  bridgeOperator: string;
  baseUsdcAddress: string;
  baseBridgeAddress: string;
  exchangeRate: string;       // Decimal as string, e.g. "1.25"
  bridgeFeePercent: string;   // e.g. "0.001"
  minBridgeAmount: string;    // e.g. "1.0"
  maxBridgeAmount: string;    // e.g. "1000000.0"
  isPaused: boolean;
}

/**
 * Build the payload to create a BridgeConfig contract on Canton.
 * The returned object is ready for sdk.cantonCreate().
 */
export function buildBridgeConfigCreate(
  adminParty: string,
  operatorParty: string,
  poolAddressBase: string
): { templateId: string; payload: BridgeConfigPayload } {
  return {
    templateId: CANTON_TEMPLATES.BridgeConfig,
    payload: {
      admin: adminParty,
      bridgeOperator: operatorParty,
      baseUsdcAddress: '0x036CbD53842c5426634e7929541eC2318f3dCF7e', // Base Sepolia USDC
      baseBridgeAddress: poolAddressBase,
      exchangeRate: '0.16',
      bridgeFeePercent: '0.001',
      minBridgeAmount: '1.0',
      maxBridgeAmount: '1000000.0',
      isPaused: false,
    },
  };
}

// ---------------------------------------------------------------------------
// Deployed pool addresses (fill after `forge create`)
// ---------------------------------------------------------------------------
export const POOL_ADDRESSES: Record<number, string> = {
  // Base Sepolia (chain ID 84532)
  84532: '0x97F9A763e1224fC195274a85E34024ac55DF71c4',
  // Ethereum Sepolia (chain ID 11155111)
  11155111: '0x97F9A763e1224fC195274a85E34024ac55DF71c4',
};

// ---------------------------------------------------------------------------
// Deployed router addresses (fill after `forge create` / deploy script)
// The router sits in front of the pool and emits CantonSwapIntent events.
// ---------------------------------------------------------------------------
export const ROUTER_ADDRESSES: Record<number, string> = {
  // Base Sepolia (chain ID 84532)
  84532: '0x8d151386339db9ced7a6cf4a5b5357315f4846ef',
  // Ethereum Sepolia (chain ID 11155111)
  11155111: '0x8d151386339db9ced7a6cf4a5b5357315f4846ef',
};

// ---------------------------------------------------------------------------
// Testnet token addresses per chain ID
// ---------------------------------------------------------------------------
export const TESTNET_TOKENS: Record<number, Record<string, string>> = {
  84532: {
    USDC: '0x036CbD53842c5426634e7929541eC2318f3dCF7e', // Base Sepolia USDC
  },
  11155111: {
    USDC: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238', // Ethereum Sepolia USDC
  },
};

// ---------------------------------------------------------------------------
// Chain name <-> chain ID mapping (testnet only)
// ---------------------------------------------------------------------------
export const CHAIN_NAME_TO_ID: Record<string, number> = {
  base: 84532,
  ethereum: 11155111,
};

export const CHAIN_ID_TO_NAME: Record<number, string> = {
  84532: 'base',
  11155111: 'ethereum',
};

// ---------------------------------------------------------------------------
// Function selectors (4-byte keccak prefixes)
//
// Computed via `cast sig` or keccak256 of the canonical signature.
// ---------------------------------------------------------------------------

// ERC-20 approve(address,uint256)
const SEL_APPROVE = '0x095ea7b3';

// TestnetSwapPool functions (verified via `cast sig`)
const SEL_SWAP = '0x8a0ccd56';                // swap(address,uint256,address,uint256)
const SEL_SWAP_ETH_FOR_TOKEN = '0xd196e9c6';  // swapETHForToken(address,uint256)
const SEL_SWAP_TOKEN_FOR_ETH = '0x094ab4ac';  // swapTokenForETH(address,uint256,uint256)
const SEL_BRIDGE_LOCK = '0x70245c28';         // bridgeLock(address,uint256,uint256,address,uint256)
const SEL_BRIDGE_LOCK_ETH = '0x1a007034';     // bridgeLockETH(uint256,address,uint256)
const SEL_BRIDGE_RELEASE = '0xddaa55a2';      // bridgeRelease(address,uint256,address,uint256)
const SEL_BRIDGE_RELEASE_ETH = '0x34c6ef34';  // bridgeReleaseETH(uint256,address,uint256)

// Canton swap intent functions (external API — sends ETH/ERC-20 and specifies Canton party ID)
const SEL_SWAP_TO_CANTON_ETH = '0x3ced1006'; // swapToCantonETH(string,uint256)
const SEL_SWAP_TO_CANTON = '0x1d0212ca';     // swapToCanton(address,uint256,string,uint256)

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

/** Pad a hex string (without 0x) to 64 hex chars (32 bytes) */
function pad32(hex: string): string {
  return hex.padStart(64, '0');
}

/** Strip 0x prefix from an address, lowercase */
function stripAddr(addr: string): string {
  return addr.replace(/^0x/i, '').toLowerCase();
}

/** Convert a bigint / number to a 32-byte hex string */
function toHex256(val: bigint | number): string {
  return pad32(BigInt(val).toString(16));
}

/** Convert a human-readable amount + decimals to raw BigInt */
export function toRaw(amount: string, decimals: number): bigint {
  const num = parseFloat(amount);
  if (isNaN(num) || num <= 0) return 0n;
  return BigInt(Math.floor(num * Math.pow(10, decimals)));
}

/** Convert raw BigInt to hex value string for tx.value (with 0x prefix) */
export function toHexValue(val: bigint): string {
  if (val === 0n) return '0x0';
  return '0x' + val.toString(16);
}

/** Check if a symbol represents native ETH (not an ERC-20) */
export function isNativeETH(symbol: string): boolean {
  return symbol.toUpperCase() === 'ETH';
}

/** Generate a unique nonce for bridge operations */
export function generateNonce(): number {
  return Date.now() * 1000 + Math.floor(Math.random() * 1000);
}

/** Get the pool address for a chain ID */
export function getPoolAddress(chainId: number): string {
  return POOL_ADDRESSES[chainId] || '';
}

/** Get the router address for a chain ID (used for Canton swap intents) */
export function getRouterAddress(chainId: number): string {
  return ROUTER_ADDRESSES[chainId] || '';
}

/** Get the ERC-20 token address for a symbol on a chain */
export function getTokenAddress(symbol: string, chainId: number): string {
  if (isNativeETH(symbol)) return '';
  return TESTNET_TOKENS[chainId]?.[symbol.toUpperCase()] || '';
}

/** Get chain ID from chain name */
export function getChainId(chainName: string): number {
  return CHAIN_NAME_TO_ID[chainName.toLowerCase()] || 0;
}

// ---------------------------------------------------------------------------
// ABI Encoding functions — each returns `data` for an EVM transaction
// ---------------------------------------------------------------------------

/** approve(address spender, uint256 amount) */
export function encodeApprove(spender: string, amount: bigint): string {
  return SEL_APPROVE + pad32(stripAddr(spender)) + toHex256(amount);
}

/** swap(address tokenIn, uint256 amountIn, address tokenOut, uint256 amountOut) */
export function encodeSwap(
  tokenIn: string,
  amountIn: bigint,
  tokenOut: string,
  amountOut: bigint
): string {
  return (
    SEL_SWAP +
    pad32(stripAddr(tokenIn)) +
    toHex256(amountIn) +
    pad32(stripAddr(tokenOut)) +
    toHex256(amountOut)
  );
}

/** swapETHForToken(address tokenOut, uint256 amountOut) */
export function encodeSwapETHForToken(tokenOut: string, amountOut: bigint): string {
  return SEL_SWAP_ETH_FOR_TOKEN + pad32(stripAddr(tokenOut)) + toHex256(amountOut);
}

/** swapTokenForETH(address tokenIn, uint256 amountIn, uint256 amountOut) */
export function encodeSwapTokenForETH(
  tokenIn: string,
  amountIn: bigint,
  amountOut: bigint
): string {
  return (
    SEL_SWAP_TOKEN_FOR_ETH +
    pad32(stripAddr(tokenIn)) +
    toHex256(amountIn) +
    toHex256(amountOut)
  );
}

/** bridgeLock(address token, uint256 amount, uint256 destChainId, address destAddress, uint256 nonce) */
export function encodeBridgeLock(
  token: string,
  amount: bigint,
  destChainId: number,
  destAddress: string,
  nonce: number
): string {
  return (
    SEL_BRIDGE_LOCK +
    pad32(stripAddr(token)) +
    toHex256(amount) +
    toHex256(destChainId) +
    pad32(stripAddr(destAddress)) +
    toHex256(nonce)
  );
}

/** bridgeLockETH(uint256 destChainId, address destAddress, uint256 nonce) */
export function encodeBridgeLockETH(
  destChainId: number,
  destAddress: string,
  nonce: number
): string {
  return (
    SEL_BRIDGE_LOCK_ETH +
    toHex256(destChainId) +
    pad32(stripAddr(destAddress)) +
    toHex256(nonce)
  );
}

/** bridgeRelease(address token, uint256 amount, address recipient, uint256 nonce) */
export function encodeBridgeRelease(
  token: string,
  amount: bigint,
  recipient: string,
  nonce: number
): string {
  return (
    SEL_BRIDGE_RELEASE +
    pad32(stripAddr(token)) +
    toHex256(amount) +
    pad32(stripAddr(recipient)) +
    toHex256(nonce)
  );
}

/** bridgeReleaseETH(uint256 amount, address recipient, uint256 nonce) */
export function encodeBridgeReleaseETH(
  amount: bigint,
  recipient: string,
  nonce: number
): string {
  return (
    SEL_BRIDGE_RELEASE_ETH +
    toHex256(amount) +
    pad32(stripAddr(recipient)) +
    toHex256(nonce)
  );
}

// ---------------------------------------------------------------------------
// Canton Swap Intent encoding (external API)
// ---------------------------------------------------------------------------

/** Encode a string as ABI dynamic bytes (offset + length + padded data) */
function encodeString(s: string): string {
  const hex = Array.from(s).map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
  const len = s.length;
  const paddedLen = Math.ceil(hex.length / 64) * 64;
  return toHex256(len) + hex.padEnd(paddedLen, '0');
}

/** swapToCantonETH(string cantonPartyId, uint256 nonce) — send ETH, receive USDC on Canton */
export function encodeSwapToCantonETH(cantonPartyId: string, nonce: number): string {
  // ABI: selector + offset_to_string + nonce + string_data
  const stringData = encodeString(cantonPartyId);
  const offset = 64; // 2 slots (offset + nonce) -> string starts at byte 64
  return (
    SEL_SWAP_TO_CANTON_ETH +
    toHex256(offset) +
    toHex256(nonce) +
    stringData
  );
}

/** swapToCanton(address token, uint256 amount, string cantonPartyId, uint256 nonce) — send ERC-20, receive USDC on Canton */
export function encodeSwapToCanton(
  token: string,
  amount: bigint,
  cantonPartyId: string,
  nonce: number
): string {
  // ABI: selector + token + amount + offset_to_string + nonce + string_data
  const stringData = encodeString(cantonPartyId);
  const offset = 128; // 4 slots (token + amount + offset + nonce) -> string starts at byte 128
  return (
    SEL_SWAP_TO_CANTON +
    pad32(stripAddr(token)) +
    toHex256(amount) +
    toHex256(offset) +
    toHex256(nonce) +
    stringData
  );
}

// ---------------------------------------------------------------------------
// EVM Pool Balance Queries (via SDK evmCall)
// ---------------------------------------------------------------------------

export interface PoolBalanceInfo {
  chainId: number;
  chainName: string;
  ethBalance: string;
  usdcBalance: string;
}

// ---------------------------------------------------------------------------
// Canton Vault Balance Queries (via SDK cantonQuery)
// ---------------------------------------------------------------------------

export interface CantonVaultBalance {
  ccBalance: string;   // CC (Splice native — requires Splice API)
  usdcBalance: string; // USDC from USDCHolding contracts
}

/** Fetch bridge operator's Canton token balances via callAPI (more reliable than cantonQuery bridge) */
export async function fetchCantonVaultBalances(sdk: SDKLike): Promise<CantonVaultBalance> {
  let usdcBalance = '0';
  let ccBalance = '0';

  // Query USDCHolding contracts owned by bridge operator via callAPI
  if (USDC_HOLDING_TEMPLATE_ID) {
    try {
      const holdings = await sdk.callAPI<Array<{ contractId: string; payload: { owner: string; amount: string } }>>(
        '/api/canton/query',
        {
          templateId: USDC_HOLDING_TEMPLATE_ID,
          filter: { owner: BRIDGE_OPERATOR_PARTY },
          readAs: [BRIDGE_OPERATOR_PARTY],
        }
      );

      let total = 0;
      for (const h of (holdings || [])) {
        const amt = parseFloat(h.payload.amount);
        if (!isNaN(amt)) total += amt;
      }
      usdcBalance = total.toFixed(2);
    } catch (e) {
      console.warn('[cantonVault] USDC query failed:', e);
      usdcBalance = '?';
    }
  } else {
    usdcBalance = 'no template';
  }

  // Query Splice Amulet (CC) contracts owned by bridge operator via callAPI
  try {
    const amulets = await sdk.callAPI<Array<{ contractId: string; payload: { owner: string; amount: { initialAmount: string } } }>>(
      '/api/canton/query',
      {
        templateId: AMULET_TEMPLATE_ID,
        filter: { owner: BRIDGE_OPERATOR_PARTY },
        readAs: [BRIDGE_OPERATOR_PARTY],
      }
    );

    let total = 0;
    for (const a of (amulets || [])) {
      const amt = parseFloat(a.payload.amount?.initialAmount || '0');
      if (!isNaN(amt)) total += amt;
    }
    ccBalance = total.toFixed(2);
  } catch (e) {
    console.warn('[cantonVault] CC (Amulet) query failed:', e);
    ccBalance = '?';
  }

  return { ccBalance, usdcBalance };
}

/** Fetch ETH + USDC balances for pool contracts on each testnet chain via SDK */
export async function fetchPoolBalances(sdk: SDKLike): Promise<PoolBalanceInfo[]> {
  const results: PoolBalanceInfo[] = [];

  for (const [chainIdStr, poolAddr] of Object.entries(POOL_ADDRESSES)) {
    const chainId = parseInt(chainIdStr);
    if (!poolAddr) continue;

    const chainName = CHAIN_ID_TO_NAME[chainId] || `Chain ${chainId}`;
    try {
      const ethRes = await sdk.evmCall({
        method: 'eth_getBalance',
        params: [poolAddr, 'latest'],
        chainId,
      });
      const ethBalance = (Number(BigInt(ethRes.result)) / 1e18).toFixed(6);

      const usdcAddr = TESTNET_TOKENS[chainId]?.USDC;
      let usdcBalance = '0';
      if (usdcAddr) {
        const data = '0x70a08231' + poolAddr.slice(2).toLowerCase().padStart(64, '0');
        const usdcRes = await sdk.evmCall({
          method: 'eth_call',
          params: [{ to: usdcAddr, data }, 'latest'],
          chainId,
        });
        usdcBalance = (Number(BigInt(usdcRes.result)) / 1e6).toFixed(2);
      }

      results.push({ chainId, chainName, ethBalance, usdcBalance });
    } catch (e) {
      console.warn(`[poolBalances] Chain ${chainId} failed:`, e);
      results.push({ chainId, chainName, ethBalance: '?', usdcBalance: '?' });
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// Canton Bridge Contract Queries (via SDK cantonQuery)
// ---------------------------------------------------------------------------

export interface BridgeContract {
  contractId: string;
  templateName: string;
  payload: Record<string, unknown>;
  status: string;
}

/** Fetch active BridgeEscrow and BridgeRequest contracts via callAPI */
export async function fetchPendingBridgeContracts(sdk: SDKLike): Promise<BridgeContract[]> {
  const contracts: BridgeContract[] = [];

  for (const [name, templateId] of Object.entries({
    BridgeEscrow: CANTON_TEMPLATES.BridgeEscrow,
    BridgeRequest: CANTON_TEMPLATES.BridgeRequest,
  })) {
    try {
      const results = await sdk.callAPI<Array<{ contractId: string; payload: Record<string, unknown> }>>(
        '/api/canton/query',
        {
          templateId,
          readAs: [BRIDGE_OPERATOR_PARTY],
        }
      );

      for (const c of (results || [])) {
        contracts.push({
          contractId: c.contractId,
          templateName: name,
          payload: c.payload,
          status: (c.payload.status as string) || 'Unknown',
        });
      }
    } catch (e) {
      console.warn(`[bridgeContracts] Failed to query ${name}:`, e);
    }
  }

  return contracts;
}

// ---------------------------------------------------------------------------
// Bridge Auto-Accept (via SDK cantonExercise)
// ---------------------------------------------------------------------------

export interface AutoAcceptResult {
  accepted: number;
  errors: string[];
}

/**
 * Find pending BridgeEscrow and BridgeRequest contracts and auto-exercise them.
 * - BridgeEscrow  -> CompleteEscrow(txHash, completionTime)
 * - BridgeRequest -> ApproveAndRelease(actualCCAmount, completionTime)
 */
export async function autoAcceptPendingContracts(sdk: SDKLike): Promise<AutoAcceptResult> {
  // Route all bridge processing through dedicated backend endpoint
  // which uses getCantonAdminClient (app-user with actAs on BridgeOperator).
  // This avoids 403 permission errors from the generic exercise endpoint.
  try {
    const result = await sdk.callAPI<{ accepted: number; errors?: string[] }>(
      '/api/canton/bridge-process'
    );
    if (result.accepted > 0) {
      console.log(`[autoAccept] Bridge processed ${result.accepted} contract(s)`);
    }
    if (result.errors && result.errors.length > 0) {
      console.warn('[autoAccept] Bridge process errors:', result.errors);
    }
    return { accepted: result.accepted, errors: result.errors || [] };
  } catch (e) {
    console.warn('[autoAccept] Bridge processing failed:', (e as Error).message);
    return { accepted: 0, errors: [(e as Error).message] };
  }
}
